/**
 * Step 3 — Affected roads
 *
 * Inline list editor for additional affected roads/sections. Mirrors the
 * "add requirement" flow with an explicit add toggle: an "Add item" button at
 * the top opens an inline draft form (country-aware `RoadDisplayField` +
 * optional description). Saving appends to `form.values.affectedRoads`; rows
 * below can be removed individually. The list is optional.
 */
import { memo, useCallback, useState } from 'react';
import { Button, Group, Stack, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { AppTextarea } from '@/components/common/AppTextarea';
import { FormSection } from '@/components/common/form';
import { RoadDisplayField } from '@/components/common/RoadDisplayField';
import { OBSTRUCTION_AFFECTED_ROAD_DESCRIPTION_MAX_LENGTH } from '@/constants/obstructions';
import {
  ICON_SIZE_SM,
  TEXTAREA_MAX_ROWS,
  TEXTAREA_MIN_ROWS,
} from '@/constants/ui';

import type { ObstructionWizardFormState } from '../../types';
import { AffectedRoadRow } from './parts/AffectedRoadRow';
import { useAffectedRoadGpsDetection } from './parts/useAffectedRoadGpsDetection';
import { useAffectedRoads } from './parts/useAffectedRoads';

import styles from './StepAffectedRoads.module.css';

interface StepAffectedRoadsProps {
  form: UseFormReturnType<ObstructionWizardFormState>;
  isLoading?: boolean;
}

export const StepAffectedRoads = memo(function StepAffectedRoads({
  form,
  isLoading = false,
}: StepAffectedRoadsProps) {
  const { t } = useTranslation();
  const { affectedRoads, totalCount, addAffectedRoad, removeAffectedRoad } =
    useAffectedRoads({ form, isLoading });

  const [isAdding, setIsAdding] = useState(false);
  const [draftLocation, setDraftLocation] = useState('');
  const [draftDescription, setDraftDescription] = useState('');

  const {
    roadSource,
    isDetectingGPS,
    gpsError,
    handleRequestGPSDetection,
    handleManualRoadInput,
  } = useAffectedRoadGpsDetection({ setLocation: setDraftLocation });

  const resetDraft = useCallback(() => {
    setDraftLocation('');
    setDraftDescription('');
    setIsAdding(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!draftLocation.trim()) return;
    addAffectedRoad(draftLocation, draftDescription);
    resetDraft();
  }, [draftLocation, draftDescription, addAffectedRoad, resetDraft]);

  const isListEmpty = totalCount === 0;
  const canSaveDraft = draftLocation.trim().length > 0 && !isLoading;

  return (
    <Stack gap="lg">
      <FormSection
        title={t('obstructions.wizard.affectedRoads.heading')}
        description={t('obstructions.wizard.affectedRoads.description')}
      >
        <Stack gap="md">
          {!isAdding && (
            <Button
              variant="light"
              leftSection={<IconPlus size={ICON_SIZE_SM} />}
              disabled={isLoading}
              onClick={() => setIsAdding(true)}
              className={styles.addButton}
            >
              {t('obstructions.wizard.affectedRoads.addItem')}
            </Button>
          )}

          {isAdding && (
            <Stack gap="sm" className={styles.draftForm}>
              <RoadDisplayField
                value={draftLocation}
                onChange={setDraftLocation}
                onManualRoadInput={handleManualRoadInput}
                onRequestGPSDetection={handleRequestGPSDetection}
                roadSource={roadSource}
                isDetectingGPS={isDetectingGPS}
                gpsError={gpsError}
                disabled={isLoading}
              />
              <AppTextarea
                label={t('obstructions.wizard.affectedRoads.descriptionLabel')}
                placeholder={t(
                  'obstructions.wizard.affectedRoads.descriptionPlaceholder'
                )}
                disabled={isLoading}
                autosize
                minRows={TEXTAREA_MIN_ROWS}
                maxRows={TEXTAREA_MAX_ROWS}
                maxLength={OBSTRUCTION_AFFECTED_ROAD_DESCRIPTION_MAX_LENGTH}
                value={draftDescription}
                onChange={(event) =>
                  setDraftDescription(event.currentTarget.value)
                }
              />
              <Group justify="flex-end" gap="sm">
                <Button
                  variant="subtle"
                  disabled={isLoading}
                  onClick={resetDraft}
                >
                  {t('obstructions.wizard.affectedRoads.cancel')}
                </Button>
                <Button disabled={!canSaveDraft} onClick={handleSave}>
                  {t('obstructions.wizard.affectedRoads.save')}
                </Button>
              </Group>
            </Stack>
          )}

          {isListEmpty && !isAdding ? (
            <Text size="sm" c="dimmed" className={styles.emptyState}>
              {t('obstructions.wizard.affectedRoads.empty')}
            </Text>
          ) : (
            <Stack gap="xs">
              {affectedRoads.map((item, index) => (
                <AffectedRoadRow
                  key={item.tempId ?? item.id}
                  item={item}
                  index={index}
                  isLoading={isLoading}
                  onRemove={removeAffectedRoad}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </FormSection>
    </Stack>
  );
});
